import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { all_routes } from "../../../../router/all_routes";
import PredefinedDateRangesOneMonth from "../../../../../core/common/datePickerOneMonth";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";
import LogsModal from "../../shared/modals/LogsModal";
import EditAttendanceModal from "../../shared/modals/EditAttendanceModal";
import EditHistoryModal from "../../shared/modals/EditHistoryModal";
import CollapseHeader from "../../../../../core/common/collapse-header/collapse-header";
import {
  getAttendenceEmployeeDetails,
  getCompanySettings,
  manualCheckIn,
} from "../../../../../services/attendanceService";
import {
  useFormattedDateTime,
  isAttendanceEditable,
} from "../../../../../helper/attendance_helper";
import { useSearchParams } from "react-router-dom";

import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { showBasicAlert } from "../../../../../core/common/useAlert";
import ConfirmationModal from "../../../../ers/common/components/ConfirmationModal";

const AttendanceEmployee = () => {
  const CHECK_IN_THRESHOLD = "09:36:00";
  const EXIT_THRESHOLD = "18:06:00";

  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get("userid");
  const companyType = searchParams.get("company_type");
  const companyId = searchParams.get("company_id");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedStartDate, setSelectedStartDate] = useState<moment.Moment>(
    moment().subtract(30, "days") // 30 days before today
  );
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment>(
    moment() // today
  );
  const [employeeDetails, setEmployeDetails] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isManualCheckin, setIsManualCheckin] = useState(false);
  const [tableData, setTableData] = useState<any>();
  const [isCheckedIn, setIsCheckedIn] = useState(
    localStorage.getItem("isCheckedIn") === "true"
  );
  const [showLogs, setShowLogs] = useState(false);
  const [activeRecord, setActiveRecord] = useState<any | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState<any>(null);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const [editHistoryRecord, setEditHistoryRecord] = useState<any>(null);
  const [monthlyStats, setMonthlyStats] = useState({
    present: 0,
    lateArrivals: 0,
    absent: 0,
    lateExits: 0,
    holidays: 0,
    weekOffs: 0,
    totalDays: 0,
  });
  const [isDefaultView, setIsDefaultView] = useState(true);
  const [filterStartDate, setFilterStartDate] = useState<moment.Moment | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<moment.Moment | null>(null);

  const user = useSelector((state: any) => {
    return state.user.user;
  });

  const isBiometricEnabledForUser =
    user?.userDetails?.is_biometric_enable ==
    process.env.REACT_APP_IS_BIOMETRIC_ENABLE;

  // Optional: keep state in sync
  useEffect(() => {
    const stored = localStorage.getItem("isCheckedIn") === "true";
    setIsCheckedIn(stored);
  }, []);

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    setFilterStartDate(start);
    setFilterEndDate(end);
    setIsDefaultView(false); // User applied a filter
  };

  const calculateCurrentMonthStats = (attendanceData: any[]) => {
    const currentMonthStart = moment().startOf('month');
    const currentMonthEnd = moment().endOf('month');

    return calculateDateRangeStats(attendanceData, currentMonthStart, currentMonthEnd);
  };

  const calculateDateRangeStats = (attendanceData: any[], start: moment.Moment, end: moment.Moment) => {
    const startDate = dayjs(start.toDate());
    const endDate = dayjs(end.toDate());
    const filteredData = attendanceData.filter(item => {
      const itemDate = dayjs(item.date);
      return itemDate.isSameOrAfter(startDate) && itemDate.isSameOrBefore(endDate);
    });

    const stats = {
      present: 0,
      lateArrivals: 0,
      absent: 0,
      lateExits: 0,
      holidays: 0,
      weekOffs: 0,
      totalDays: 0
    };

    filteredData.forEach(item => {
      stats.totalDays++; // Count all days in the filtered range

      // Count holidays and week offs
      if (item.status === 'Holiday') {
        stats.holidays++;
      } else if (item.status === 'Week Off') {
        stats.weekOffs++;
      }
      // Status-based counts
      else if (item.status === 'Present') {
        stats.present++;

        // Late arrival check
        if (item.check_in_time && !['Week Off', 'Holiday', 'Absent', 'N/A'].includes(item.check_in_time)) {
          const checkIn = dayjs(item.check_in_time, 'HH:mm:ss');
          const lateThreshold = dayjs(CHECK_IN_THRESHOLD, 'HH:mm:ss');
          if (checkIn.isAfter(lateThreshold)) {
            stats.lateArrivals++;
          }
        }

        // Late exit check
        if (item.check_out_time && !['Week Off', 'Holiday', 'Absent', 'N/A'].includes(item.check_out_time)) {
          const checkOut = dayjs(item.check_out_time, 'HH:mm:ss');
          const exitThreshold = dayjs(EXIT_THRESHOLD, 'HH:mm:ss');
          if (checkOut.isAfter(exitThreshold)) {
            stats.lateExits++;
          }
        }
      }
      else if (item.status === 'Late') {
        stats.present++;
        stats.lateArrivals++;

        // Check for late exit for Late status too
        if (item.check_out_time && !['Week Off', 'Holiday', 'Absent', 'N/A'].includes(item.check_out_time)) {
          const checkOut = dayjs(item.check_out_time, 'HH:mm:ss');
          const exitThreshold = dayjs(EXIT_THRESHOLD, 'HH:mm:ss');
          if (checkOut.isAfter(exitThreshold)) {
            stats.lateExits++;
          }
        }
      }
      else if (item.status === 'Absent') {
        stats.absent++;
      }
    });

    return stats;
  };

  useEffect(() => {
    if (employeeDetails?.attendance) {
      const currentMonthStats = calculateCurrentMonthStats(employeeDetails.attendance);

      if (!isDefaultView && filterStartDate && filterEndDate) {
        const filteredStats = calculateDateRangeStats(
          employeeDetails.attendance,
          filterStartDate,
          filterEndDate
        );
        setMonthlyStats(filteredStats);
      } else {
        setMonthlyStats(currentMonthStats);
      }
    }
  }, [employeeDetails?.attendance, selectedStartDate, selectedEndDate, filterStartDate, filterEndDate, isDefaultView]);

  const formatDateRange = () => {
    if (isDefaultView) {
      return moment().format('MMM YYYY');
    }

    if (filterStartDate && filterEndDate) {
      const start = filterStartDate.format('MMM D, YYYY');
      const end = filterEndDate.format('MMM D, YYYY');

      const daysDiff = filterEndDate.diff(filterStartDate, 'days');
      const isOneMonth = daysDiff >= 28 && daysDiff <= 31;

      if (isOneMonth && filterStartDate.date() === 1) {
        return filterStartDate.format('MMM YYYY');
      }

      return `${start} - ${end}`;
    }

    return moment().format('MMM YYYY'); // Fallback
  };

  useEffect(() => {
    setIsLoading(true);
    getUserDetails();
    getUserComapanydetails();
  }, [selectedStartDate, userId, isCheckedIn]);

  const getTodaysAttendance = (attendanceData: any[]) => {
    if (!attendanceData) return null;
    const today = dayjs().format("YYYY-MM-DD");
    return attendanceData.find((item) => item.date === today);
  };
  const getUserComapanydetails = async () => {
    try {
      const res = await getCompanySettings();
      if (res.data?.data?.data?.settings?.biometricEnabled !== undefined) {
        setIsManualCheckin(!res.data.data.data.settings.biometricEnabled);
      } else {
        setIsManualCheckin(true);
      }
    } catch (error) {
      console.error("Error fetching company settings:", error);
      setIsManualCheckin(true);
    }
  };
  const getUserDetails = async () => {
    try {
      const res = await getAttendenceEmployeeDetails(
        userId,
        selectedStartDate,
        selectedEndDate,
        companyId,
        companyType
      );
      setEmployeDetails(res.data);

      const mapped = res.data.attendance.map((item: any, index: number) => {
        let status = item.status;

        if (item.status === "Present" && item.check_in_time) {
          const lateMinutes = Math.max(
            dayjs(item.check_in_time, "HH:mm:ss").diff(
              dayjs(CHECK_IN_THRESHOLD, "HH:mm:ss"),
              "minute"
            ),
            0
          );
          status = lateMinutes > 0 ? "Late" : "Present";
        }

        return {
          key: index.toString(),
          id: item.id,
          user_id: res.data.user_id,
          essl_id: item.essl_id,
          Date: item.date,
          Status: status,
          CheckIn: formatTime(item.check_in_time),
          CheckOut: formatTime(item.check_out_time),
          raw_check_in: item.check_in_time,
          raw_check_out: item.check_out_time,
          Break: item.break_hours,
          Late:
            status === "Late"
              ? `${Math.max(
                dayjs(item.check_in_time, "HH:mm:ss").diff(
                  dayjs(CHECK_IN_THRESHOLD, "HH:mm:ss"),
                  "minute"
                ),
                0
              )} Min`
              : "0 Min",
          Production: item.total_hours,
          ProductionHours: item.productive_hours,
          LateMinutes: status === "Late" ? 1 : 0,
          logs: item.logs,
          edit_logs: item.edit_logs || [],
          holidayTitle: item.holiday_title || "",
          holidayDescription: item.holiday_description || "",
          leaveInfo: item.leave_info || null,
          isLeaveDay: item.leave_info?.isLeaveDay || false,
        };
      });

      const sorted = mapped.sort(
        (a: any, b: any) => dayjs(b.Date).valueOf() - dayjs(a.Date).valueOf()
      );

      setTableData(sorted);
      setIsLoading(false);
    } catch (error) {
      console.log(error, "error");
    }
  };
  const formatTime = (value: string | null | undefined) => {
    if (!value || value === "Week Off" || value === "Holiday") {
      return "N/A";
    }
    return dayjs(value, "HH:mm:ss").isValid()
      ? dayjs(value, "HH:mm:ss").format("hh:mm A")
      : "N/A";
  };

  const filteredTableData = selectedStatus
    ? tableData?.filter((row: any) => {
      if (selectedStatus === "Present") {
        return row.Status === "Present" || row.Status === "Late";
      }
      if (selectedStatus === "Late") {
        return row.Status === "Late";
      }
      if (selectedStatus === "Holiday") {
        return row.Status === "Holiday";
      }
      if (selectedStatus === "Week Off") {
        return row.Status === "Week Off";
      }
      return row.Status === selectedStatus;
    })
    : tableData;

  const columns: ColumnsType<any> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_: any, __: any, index: number) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      width: 150,
      fixed: "left",
      render: (text: string) => {
        const date = moment(text);
        return (
          <div className="d-flex flex-column">
            <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
              {date.format("DD-MM-YYYY")}
            </span>
            <span style={{ fontSize: 11.5, color: "#94a3b8" }}>
              {date.format("dddd")}
            </span>
          </div>
        );
      },
      sorter: (a: any, b: any) =>
        moment(a.Date).valueOf() - moment(b.Date).valueOf(),
    },

    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 170,
      render: (text: string, record: any) => {
        let badgeClass = "badge d-inline-flex align-items-center";
        let customStyle: React.CSSProperties = {};

        if (text === "Present") {
          badgeClass += " badge-success-transparent";
        } else if (text === "Late") {
          customStyle = {
            backgroundColor: "#ff6700",
            color: "#ffffff",
            borderRadius: "4px",
            padding: "2px 6px",
            fontWeight: 500,
          };
        } else if (text === "Week Off") {
          badgeClass += " badge-info-transparent";
        } else if (text === "Holiday") {
          badgeClass += " badge-warning-transparent";
        }
        else if (text === "Leave") {
          // ✅ Purple for Leave — no longer falls into red/danger
          customStyle = {
            backgroundColor: "#f3e8ff",
            color: "#7c3aed",
            border: "1px solid #d8b4fe",
            borderRadius: "4px",
            padding: "2px 6px",
            fontWeight: 500,
          };
        } else {
          badgeClass += " badge-danger-transparent";
        }

        const leaveSubType = getLeaveSubType(record.leaveInfo);

        const showSubBadge =
          leaveSubType !== null &&
          (record.Status === "Leave" || record.isLeaveDay);

        return (
          <div>
            <span className={badgeClass} style={customStyle}>
              <i className="ti ti-point-filled me-1" />
              {record.Status}
            </span>

            {showSubBadge && leaveSubType && (
              <div style={{ marginTop: "4px" }}>
                <span
                  className="badge"
                  style={{
                    ...leaveSubType.style,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontSize: "11px",
                  }}
                >
                  <i className="ti ti-calendar-minus me-1" />
                  {leaveSubType.label}
                </span>
              </div>
            )}

            {/* Holiday info unchanged */}
            {record.Status === "Holiday" && record.holidayTitle && (
              <div style={{ fontSize: "12px", color: "#856404", marginTop: "2px" }}>
                <strong>{record.holidayTitle}</strong>
                {record.holidayDescription && (
                  <div style={{ fontSize: "11px", color: "#6c757d" }}>
                    {record.holidayDescription}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },

    {
      title: "Check In",
      dataIndex: "CheckIn",
      key: "CheckIn",
      width: 110,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13, color: text && text !== "N/A" ? "#0f172a" : "#94a3b8" }}>
          {text || "—"}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a.CheckIn || "").localeCompare(b.CheckIn || ""),
    },
    {
      title: "Check Out",
      dataIndex: "CheckOut",
      key: "CheckOut",
      width: 110,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13, color: text && text !== "N/A" ? "#0f172a" : "#94a3b8" }}>
          {text || "—"}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a.CheckOut || "").localeCompare(b.CheckOut || ""),
    },
    {
      title: "Break",
      dataIndex: "Break",
      key: "Break",
      width: 110,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13, color: text && text !== "N/A" ? "#0f172a" : "#94a3b8" }}>
          {text || "—"}
        </span>
      ),
      sorter: (a: any, b: any) => (a.Break || "").localeCompare(b.Break || ""),
    },
    {
      title: "Late",
      dataIndex: "Late",
      key: "Late",
      width: 90,
      align: "center",
      render: (text: string) => {
        if (!text || text === "N/A" || text === "0 Min" || text === "0m") {
          return <span style={{ fontSize: 13, color: "#94a3b8" }}>—</span>;
        }
        return (
          <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
            {text}
          </Tag>
        );
      },
      sorter: (a: any, b: any) => (a.Late || "").localeCompare(b.Late || ""),
    },
    {
      title: "Production Hours",
      dataIndex: "ProductionHours",
      key: "ProductionHours",
      width: 160,
      align: "center",
      render: (_: string, record: any) => {
        const mins = toMinute(record.ProductionHours);
        let color: "default" | "success" | "warning" | "error" | "processing" = "default";
        let displayText = record.ProductionHours;

        if (record.CheckOut === "N/A" && mins > 8 * 60) {
          color = "warning";
          displayText = "(No Checkout)";
        } else if (mins < 8 * 60) {
          color = "error";
        } else if (mins <= 9 * 60) {
          color = "success";
        } else {
          color = "processing";
        }

        return (
          <Tag
            color={color}
            style={{ fontSize: 11.5, margin: 0, display: "inline-flex", alignItems: "center" }}
          >
            <i className="ti ti-clock-hour-11 me-1" />
            {displayText}
          </Tag>
        );
      },
      sorter: (a: any, b: any) =>
        toMinutes(a.ProductionHours) - toMinutes(b.ProductionHours),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      align: "center",
      fixed: "right",
      render: (_text: string, record: any) => {
        const editability = isAttendanceEditable(record);

        return (
          <div className="action-icon d-inline-flex">
            {editability.editable ? (
              <button
                type="button"
                className="btn btn-link p-0 ms-1"
                title="Edit Attendance"
                onClick={() => {
                  setEditRecord({
                    ...record,
                    date: record.Date,
                    CheckIn: record.raw_check_in,
                    CheckOut: record.raw_check_out,
                  });
                  setShowEditModal(true);
                }}
              >
                <i className="ti ti-edit" />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-link p-0 ms-1"
                disabled
                title={editability.reason || "Not editable"}
                style={{ opacity: 0.4 }}
              >
                <i className="ti ti-edit" />
              </button>
            )}
            {record.edit_logs && record.edit_logs.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 ms-1"
                title="View Edit History"
                onClick={() => {
                  setEditHistoryRecord(record);
                  setShowEditHistory(true);
                }}
              >
                <i className="ti ti-history" />
              </button>
            )}
            <button
              type="button"
              className="btn btn-link p-0 ms-1"
              title="View Logs"
              onClick={() => {
                setActiveRecord(record);
                setShowLogs(true);
              }}
            >
              <i className="ti ti-clipboard-list" />
            </button>
          </div>
        );
      },
    },
  ];

  // Function to render the timeline bars
  type TimelineBlock = {
    check_time: string; // "HH:mm:ss"
    type?: "check-in" | "check-out";
  };

  const DAY_START_MIN = 6 * 60; // 06:00
  const OT_START_MIN = 18 * 60; // 18:00
  const DAY_END_MIN = 24 * 60; // 24:00
  const TOTAL_WINDOW_MIN = DAY_END_MIN - DAY_START_MIN; // 1080 min = 18h

  // "HH:mm:ss" -> minutes from midnight (0-1440)
  const toMinutes = (hhmmss: string) => {
    const [h, m, s] = hhmmss.split(":").map(Number);
    if ([h, m, s].some((n) => Number.isNaN(n))) return NaN;
    return h * 60 + m + (s ? Math.floor(s / 60) : 0);
  };

  const toMinute = (val: unknown): number => {
    if (val == null) return 0;
    const s = String(val).trim().toLowerCase();
    if (!s) return 0;

    // match "4h", "4 hrs", "4hour", "4.5h", etc.
    const hMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:h(?:ours?)?|hrs?|hr)\b/);
    // match "11m", "11 min", "11 minutes"
    const mMatch = s.match(/(\d+)\s*m(?:in(?:utes)?)?\b/);

    let mins = 0;
    if (hMatch) mins += Math.round(parseFloat(hMatch[1]) * 60);
    if (mMatch) mins += parseInt(mMatch[1], 10);

    // fallback: if string is like "8.25" or "8.25 Hrs" without explicit "h"
    if (!hMatch && !mMatch) {
      const n = parseFloat(s);
      if (!Number.isNaN(n)) mins += Math.round(n * 60);
    }
    return mins;
  };

  // width % helper
  const widthPct = (durMin: number) => (durMin / TOTAL_WINDOW_MIN) * 100;

  const renderTimelineBars = (timelineBlocks: TimelineBlock[] = []) => {
    if (!timelineBlocks || timelineBlocks.length === 0) return null;

    // 1) Sort by time using minutes
    const sorted = [...timelineBlocks]
      .map((t) => ({ ...t, _min: toMinutes(t.check_time) }))
      .filter((t) => !Number.isNaN(t._min)) // drop invalid times
      .sort((a, b) => a._min - b._min);

    if (sorted.length === 0) return null;

    // 2) Assign alternating types (check-in, check-out, check-in, ...)
    for (let i = 0; i < sorted.length; i++) {
      sorted[i].type = i % 2 === 0 ? "check-in" : "check-out";
    }

    // 3) Build segments (working between in→out, break between out→in)
    type Seg = { type: "working" | "break"; startMin: number; endMin: number };
    const segments: Seg[] = [];

    for (let i = 0; i < sorted.length - 1; i++) {
      const cur = sorted[i];
      const nxt = sorted[i + 1];

      if (!cur.type || !nxt.type) continue;
      if (cur._min >= nxt._min) continue; // ignore non-forward

      if (cur.type === "check-in" && nxt.type === "check-out") {
        segments.push({
          type: "working",
          startMin: cur._min,
          endMin: nxt._min,
        });
      } else if (cur.type === "check-out" && nxt.type === "check-in") {
        segments.push({ type: "break", startMin: cur._min, endMin: nxt._min });
      }
    }

    // 4) Create bars within the 06:00–24:00 window
    const bars: JSX.Element[] = [];

    // Initial white space from 06:00 to first event
    const firstMin = sorted[0]._min;
    if (firstMin > DAY_START_MIN) {
      const durMin = Math.max(
        0,
        Math.min(firstMin, DAY_END_MIN) - DAY_START_MIN
      );
      if (durMin > 0) {
        bars.push(
          <div
            key="initial-space"
            className="progress-bar bg-info-transparent rounded"
            role="progressbar"
            style={{ width: `${widthPct(durMin)}%` }}
          />
        );
      }
    }

    // Add all segments (clamped to window)
    segments.forEach((seg, i) => {
      // Clamp to [06:00, 24:00]
      const startClamped = Math.min(
        Math.max(seg.startMin, DAY_START_MIN),
        DAY_END_MIN
      );
      const endClamped = Math.min(
        Math.max(seg.endMin, DAY_START_MIN),
        DAY_END_MIN
      );
      const durMin = Math.max(0, endClamped - startClamped);
      if (durMin <= 0) return;

      if (seg.type === "working") {
        // Split around OT start (18:00)
        const regularEnd = Math.min(endClamped, OT_START_MIN);
        const overtimeStart = Math.max(startClamped, OT_START_MIN);

        const regularDur = Math.max(0, regularEnd - startClamped);
        const overtimeDur = Math.max(0, endClamped - overtimeStart);

        if (regularDur > 0) {
          bars.push(
            <div
              key={`seg-${i}-reg`}
              className="progress-bar bg-success rounded me-2"
              role="progressbar"
              style={{ width: `${widthPct(regularDur)}%` }}
              title="Working"
            />
          );
        }

        if (overtimeDur > 0) {
          bars.push(
            <div
              key={`seg-${i}-ot`}
              className="progress-bar bg-info rounded me-2"
              role="progressbar"
              style={{ width: `${widthPct(overtimeDur)}%` }}
              title="Overtime"
            />
          );
        }
      } else {
        // Break
        bars.push(
          <div
            key={`seg-${i}-break`}
            className="progress-bar bg-warning rounded me-2"
            role="progressbar"
            style={{ width: `${widthPct(durMin)}%` }}
            title="Break"
          />
        );
      }
    });

    // Final white space from last event to 24:00
    const lastMin = sorted[sorted.length - 1]._min;
    if (lastMin < DAY_END_MIN) {
      const durMin = Math.max(
        0,
        DAY_END_MIN - Math.max(lastMin, DAY_START_MIN)
      );
      if (durMin > 0) {
        bars.push(
          <div
            key="final-space"
            className="progress-bar bg-info-transparent rounded"
            role="progressbar"
            style={{ width: `${widthPct(durMin)}%` }}
          />
        );
      }
    }

    return bars;
  };

  const getLastActionType = (logs: any | null) => {
    if (!logs || logs.length === 0) return null;
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
    );

    const lastIndex = sortedLogs.length - 1;

    return sortedLogs.length % 2 === 1 ? "check-in" : "check-out";
  };
  const shouldShowPunchIn = (): boolean => {
    const todaysAttendance = getTodaysAttendance(employeeDetails?.attendance);

    if (!todaysAttendance) return true; // New day, no attendance record

    const logs = todaysAttendance.logs || [];
    if (logs.length === 0) return true; // No actions today

    const lastActionType = getLastActionType(logs);

    return lastActionType === "check-out" || !lastActionType;
  };

  const getCurrentLocation = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        maximumAge: 0,
      });
    });
  };

  const getUserIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Failed to fetch IP Address:", error);
      return null;
    }
  };

  const handlePunchAction = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // First attempt to get location
      const position = await getCurrentLocation();
      const userIp = await getUserIpAddress();

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };
      const ip = userIp;

      await manualCheckIn(location, ip);
      await getUserDetails();
    } catch (error: any) {
      console.error("Punch action failed: inside catch block");
      console.log(error.message);
      showBasicAlert(error.message);
    } finally {
      // Always reset processing state
      setIsProcessing(false);
    }
  };

  const showPunchIn = shouldShowPunchIn();
  const confirmationTitle = showPunchIn ? "Punch In" : "Punch Out";
  const confirmationMessage = showPunchIn
    ? "Are you ready to Punch In and start your working hours?"
    : "Are you sure you want to Punch Out and end your working hours?";

  const handleCloseModal = useCallback(() => {
    setConfirmationModalOpen(false);
  }, []);

  const LEAVE_PART_CONFIG: Record<string, { label: string; style: React.CSSProperties } | null> = {
    "full|full": {
      label: "Full Day",
      style: { backgroundColor: "#faf5ff", color: "#7c3aed", border: "1px solid #d8b4fe" },
    },
    "full|first_half": {
      label: "Till 1st Half",
      style: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74" },
    },

    "full|second_half": {
      label: "Till 2nd Half",
      style: { backgroundColor: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" },
    },

    "first_half|first_half": {
      label: "1st Half",
      style: { backgroundColor: "#eff6ff", color: "#1d4ed8", border: "1px solid #93c5fd" },
    },

    "first_half|full": {
      label: "From 1st Half",
      style: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74" },
    },

    "second_half|second_half": {
      label: "2nd Half",
      style: { backgroundColor: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" },
    },

    "second_half|full": {
      label: "From 2nd Half",
      style: { backgroundColor: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" },
    },

    "second_half|first_half": {
      label: "2nd Half → 1st Half",
      style: { backgroundColor: "#fdf4ff", color: "#7c3aed", border: "1px solid #d8b4fe" },
    },

    "first_half|second_half": {
      label: "1st Half → 2nd Half",
      style: { backgroundColor: "#fdf4ff", color: "#7c3aed", border: "1px solid #d8b4fe" },
    },
  };

  const getLeaveSubType = (leaveInfo: any): { label: string; style: React.CSSProperties } | null => {
    if (!leaveInfo) return null;
    const { startDayPart, endDayPart, leave_name } = leaveInfo;

    const key = `${startDayPart}|${endDayPart}`;
    const config = LEAVE_PART_CONFIG[key];

    if (config === undefined) return null;

    if (config === null) return null;

    return {
      label: `${config.label}${leave_name ? ` · ${leave_name}` : ""}`,
      style: config.style,
    };
  };

  const renderPunchButton = () => {
    if (!isManualCheckin || userId) {
      if (isBiometricEnabledForUser) {
        return null;
      }
    }

    const showPunchIn = shouldShowPunchIn();
    const buttonText = showPunchIn ? "Punch In" : "Punch Out";
    const buttonClass = showPunchIn ? "btn-dark" : "btn-outline-dark";

    return (
      <button
        type="button"
        className={`btn ${buttonClass} w-50 mb-2`}
        onClick={() => setConfirmationModalOpen(true)}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Processing...
          </>
        ) : (
          buttonText
        )}
      </button>
    );
  };
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    if (hour < 21) return "Good Evening";
    return "Good Night";
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Employee Attendance</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Employee Attendance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-info d-inline-flex align-items-center"
                onClick={() => navigate(-1)}
                style={{ minHeight: 38 }}
              >
                <i className="ti ti-arrow-back me-1" />
                Go Back
              </button>
              <div className="head-icons mb-0">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row g-3">
            <div className="col-xl-3 col-lg-4 d-flex">
              <div
                className="flex-fill"
                style={{
                  background: "#ffffff",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                }}
              >
                <div className="mb-3 text-center">
                  <p
                    className="mb-1"
                    style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}
                  >
                    {getGreeting()}, {employeeDetails?.name}
                  </p>
                  <h4 className="mb-0" style={{ fontSize: 16, color: "#0f172a" }}>
                    {useFormattedDateTime()}
                  </h4>
                </div>
                <div
                  className="attendance-circle-progress mx-auto mb-3"
                  data-value={65}
                >
                  <span className="progress-left">
                    <span className="progress-bar border-success" />
                  </span>
                  <span className="progress-right">
                    <span className="progress-bar border-success" />
                  </span>
                  <div className="avatar avatar-xxl avatar-rounded">
                    <ImageWithBasePath
                      src={
                        employeeDetails?.userProfile ||
                        "assets/img/profiles/avatar-27.jpg"
                      }
                      alt="Img"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <span
                    className="d-inline-flex align-items-center mb-3"
                    style={{
                      background: "#eff6ff",
                      color: "#1d71e3",
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <i className="ti ti-clock-hour-4 me-1" />
                    Production: {employeeDetails?.today_total_hours || 0}
                  </span>
                  {isManualCheckin && !userId && (
                    <div className="mt-2">{renderPunchButton()}</div>
                  )}
                  {!isBiometricEnabledForUser && (
                    <div className="mt-2">{renderPunchButton()}</div>
                  )}
                  {!isManualCheckin && (
                    <h6
                      className="fw-medium d-flex align-items-center justify-content-center mt-2 mb-0"
                      style={{ fontSize: 13 }}
                    >
                      <i className="ti ti-fingerprint text-primary me-1" />
                      {employeeDetails?.today_punch_in !== "Absent"
                        ? `Punch In at ${employeeDetails?.today_punch_in}`
                        : "Absent"}
                    </h6>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-9 col-lg-8 d-flex">
              <div className="row flex-fill g-3">
                <div className="col-xl-4 col-md-6 d-flex">
                  <div
                    className="flex-fill d-flex flex-column justify-content-center"
                    style={{
                      background: "#ffffff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: "#e0f2fe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 12,
                      }}
                    >
                      <i
                        className="ti ti-calendar-up"
                        style={{ color: "#0284c7", fontSize: 20 }}
                      />
                    </div>
                    <h2
                      className="mb-1"
                      style={{ fontSize: 30, fontWeight: 700, color: "#0f172a" }}
                    >
                      {employeeDetails?.total_hours_month}
                    </h2>
                    <p
                      className="mb-0"
                      style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}
                    >
                      Total Hours Month
                    </p>
                  </div>
                </div>

                <div className="col-xl-8 col-md-6 d-flex">
                  <div
                    className="flex-fill"
                    style={{
                      background: "#ffffff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "20px",
                    }}
                  >
                    <div className="d-flex align-items-center mb-3">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: "#eef2ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 10,
                        }}
                      >
                        <i
                          className="ti ti-calendar-stats"
                          style={{ color: "#6366f1", fontSize: 18 }}
                        />
                      </div>
                      <div>
                        <h6
                          className="mb-0"
                          style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}
                        >
                          Attendance Summary
                        </h6>
                        <p
                          className="mb-0"
                          style={{ fontSize: 11, color: "#94a3b8" }}
                        >
                          {formatDateRange()}
                        </p>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      {[
                        { label: "Present", value: monthlyStats.present, color: "#16a34a" },
                        { label: "Late", value: monthlyStats.lateArrivals, color: "#f59e0b" },
                        { label: "Absent", value: monthlyStats.absent, color: "#dc2626" },
                        { label: "Late Exits", value: monthlyStats.lateExits, color: "#0ea5e9" },
                      ].map((s) => (
                        <div className="col-3" key={s.label}>
                          <div
                            style={{
                              background: "#f8fafc",
                              borderRadius: 8,
                              padding: "10px 6px",
                              textAlign: "center",
                            }}
                          >
                            <h3
                              className="mb-0"
                              style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}
                            >
                              {s.value}
                            </h3>
                            <p
                              className="mb-0 mt-1"
                              style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}
                            >
                              {s.label}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      className="d-flex justify-content-between align-items-center flex-wrap"
                      style={{ paddingTop: 12, borderTop: "1px solid #f1f5f9", gap: 8 }}
                    >
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        Working Days:{" "}
                        <strong style={{ color: "#0f172a" }}>
                          {monthlyStats.present + monthlyStats.absent}
                        </strong>{" "}
                        · Total:{" "}
                        <strong style={{ color: "#0f172a" }}>
                          {monthlyStats.totalDays}
                        </strong>
                      </span>
                      <div className="d-flex gap-2">
                        {monthlyStats.holidays > 0 && (
                          <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>
                            Holidays: {monthlyStats.holidays}
                          </Tag>
                        )}
                        {monthlyStats.weekOffs > 0 && (
                          <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>
                            Week Off: {monthlyStats.weekOffs}
                          </Tag>
                        )}
                        {employeeDetails?.summary?.total_leaves > 0 && (
                          <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                            Leaves: {employeeDetails.summary.total_leaves}
                          </Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 d-flex">
                  <div
                    className="flex-fill"
                    style={{
                      background: "#ffffff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "20px",
                    }}
                  >
                    <div className="row mb-3">
                      <div className="col-xl-3">
                        <p
                          className="d-flex align-items-center mb-1"
                          style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}
                        >
                          <i className="ti ti-point-filled text-dark-transparent me-1" />
                          Today Working hours
                        </p>
                        <h3 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
                          {employeeDetails?.today_total_hours}
                        </h3>
                      </div>
                      <div className="col-xl-3">
                        <p
                          className="d-flex align-items-center mb-1"
                          style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}
                        >
                          <i className="ti ti-point-filled text-success me-1" />
                          Today Productive Hours
                        </p>
                        <h3 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
                          {employeeDetails?.total_today_productive}
                        </h3>
                      </div>
                      <div className="col-xl-3">
                        <p
                          className="d-flex align-items-center mb-1"
                          style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}
                        >
                          <i className="ti ti-point-filled text-warning me-1" />
                          Today Break hours
                        </p>
                        <h3 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
                          {employeeDetails?.total_today_break}
                        </h3>
                      </div>
                    </div>
                    <div
                      className="progress bg-transparent-dark mb-2"
                      style={{ height: 22, borderRadius: 6 }}
                    >
                      {renderTimelineBars(employeeDetails?.timeline_blocks)}
                    </div>
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                      {Array.from({ length: 18 }, (_, i) => (
                        <span
                          key={i}
                          style={{ fontSize: 10, color: "#94a3b8" }}
                        >{`${(i + 6) % 12 || 12}:00 ${i < 6 ? "AM" : "PM"}`}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h4
                    className="mb-1 d-flex align-items-center"
                    style={{ fontSize: 17, fontWeight: 600, color: "#0f172a" }}
                  >
                    <i
                      className="ti ti-clock-check text-info me-2"
                      style={{ fontSize: 20 }}
                    />
                    Attendance Records
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    Daily attendance history — filter by date range or status
                  </p>
                </div>
              </div>
            </div>

            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <div className="input-icon-end position-relative me-2">
                  <PredefinedDateRangesOneMonth
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    onDateChange={handleDateChange}
                  />
                  <span className="input-icon-addon">
                    <i className="ti ti-chevron-down" />
                  </span>
                </div>
                {!isDefaultView && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-inline-flex align-items-center mb-2"
                    style={{ minHeight: 38 }}
                    onClick={() => {
                      setIsDefaultView(true);
                      setFilterStartDate(null);
                      setFilterEndDate(null);
                      setSelectedStartDate(moment().startOf("month"));
                      setSelectedEndDate(moment().endOf("month"));
                    }}
                  >
                    <i className="ti ti-arrow-back-up me-1" />
                    Reset to Current Month
                  </button>
                )}
              </div>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-2">
                <div className="me-2 mb-2">
                  <Select
                    placeholder="Select Status"
                    style={{ width: 180, minHeight: 38 }}
                    value={selectedStatus ?? undefined}
                    allowClear
                    onChange={(value) => {
                      setSelectedStatus(value ?? null);
                      setPage(1);
                    }}
                    options={[
                      { value: "Present", label: "Present" },
                      { value: "Absent", label: "Absent" },
                      { value: "Late", label: "Late" },
                      { value: "Week Off", label: "Week Off" },
                      { value: "Holiday", label: "Holiday" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card-body p-0 mb-3 border-0">
            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center bg-white"
                style={{
                  minHeight: 400,
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="text-center">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                  <p className="mt-3 text-muted">Loading attendance...</p>
                </div>
              </div>
            ) : (filteredTableData?.length ?? 0) > 0 ? (
              <div
                className="bg-white"
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Table
                  className="custom-data-table"
                  columns={columns}
                  dataSource={(filteredTableData ?? []).slice(
                    (page - 1) * limit,
                    page * limit,
                  )}
                  rowKey={(record: any) => record.Date}
                  size="middle"
                  pagination={false}
                  scroll={{ x: 1200 }}
                  rowClassName={(_, index) =>
                    index % 2 === 0
                      ? "data-table-row-light"
                      : "data-table-row-dark"
                  }
                />
                <CustomPagination
                  currentPage={page}
                  totalPages={Math.max(
                    1,
                    Math.ceil((filteredTableData?.length ?? 0) / limit),
                  )}
                  total={filteredTableData?.length ?? 0}
                  limit={limit}
                  hasNextPage={page * limit < (filteredTableData?.length ?? 0)}
                  hasPrevPage={page > 1}
                  onPageChange={(p) => setPage(p)}
                  onLimitChange={(l) => {
                    setLimit(l);
                    setPage(1);
                  }}
                />
              </div>
            ) : (
              <div
                className="d-flex justify-content-center align-items-center bg-white"
                style={{
                  minHeight: 320,
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                }}
              >
                <div className="text-center">
                  <i
                    className="ti ti-calendar-off"
                    style={{ fontSize: 48, color: "#cbd5e1" }}
                  />
                  <h5 className="mt-3 mb-2 fw-semibold">No attendance records</h5>
                  <p className="text-muted mb-0">
                    Try adjusting the date range or status.
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <LogsModal
        open={showLogs}
        onClose={() => setShowLogs(false)}
        employee={activeRecord?.Employee}
        logs={activeRecord?.logs}
      />

      <EditAttendanceModal
        visible={showEditModal}
        onClose={() => setShowEditModal(false)}
        record={editRecord}
        onSuccess={() => {
          setShowEditModal(false);
          getUserDetails();
        }}
      />

      <EditHistoryModal
        visible={showEditHistory}
        onClose={() => setShowEditHistory(false)}
        editLogs={editHistoryRecord?.edit_logs || []}
        employeeName={editHistoryRecord?.Employee}
        date={editHistoryRecord?.Date}
      />
      <ConfirmationModal
        isOpen={confirmationModalOpen}
        onClose={handleCloseModal}
        message={confirmationMessage}
        title={confirmationTitle}
        onConfirm={handlePunchAction}
        type="info"
      />
    </>
  );
};

export default AttendanceEmployee;
